import { inject, injectable } from 'inversify';
import { DataConsumer } from 'mediasoup-client/lib/DataConsumer';
import { serializeError } from 'serialize-error';
import { Logger } from '../types/common';
import { TOKEN } from '../inversify.tokens';
import { ChannelMessage } from '../proto/generated/channel_message/channel_message';
import { ChannelEvent, channelEventTypeToJSON } from '../proto/generated/channel_message/channel_event';
import { MediaEvent, mediaEventTypeToJSON } from '../proto/generated/channel_message/media_event';

interface PendingMessage {
  total: number;
  received: Uint8Array[];
  msgId: number;
}

const CHUNK_HEADER_SIZE = 12;
const CHUNK_HEADER_MSG_ID_OFFSET = 0;
const CHUNK_HEADER_INDEX_OFFSET = 4;
const CHUNK_HEADER_TOTAL_OFFSET = 8;

@injectable()
export default class EventsQueue {
  readonly #queue: ChannelMessage[] = [];

  #paused = false;

  #processing = false;

  #mediaEventHandler?: (event: MediaEvent) => void | Promise<void>;

  #channelEventHandler?: (event: ChannelEvent) => void | Promise<void>;

  readonly #pendingMessagesBuffer = new Map<number, PendingMessage>();
  readonly #logger: Logger;

  #processedMessageIds: Set<number> = new Set<number>();

  constructor(@inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger) {
    this.#logger = loggerFactory('EventsQueue');
  }

  setMediaEventHandler(handler: (event: MediaEvent) => void | Promise<void>): void {
    this.#mediaEventHandler = handler;
    this.#logger.info('Media event handler set', {
      case: 'EventsQueue',
    });
  }

  setChannelEventHandler(handler: (event: ChannelEvent) => void | Promise<void>): void {
    this.#channelEventHandler = handler;
    this.#logger.info('Channel event handler set', {
      case: 'EventsQueue',
    });
  }

  subscribe(dataChannel: DataConsumer): void {
    this.#processedMessageIds = new Set<number>();
    let lastReceivedMsgId = 0;

    const onMessage = (raw: ArrayBuffer): void => {
      try {
        const pendingMessage = this.processMessageChunk(raw);

        if (!pendingMessage || !this.isPendingMessageFullyReceived(pendingMessage)) {
          return;
        }

        const fullBytes = this.finalizeMessageBytes(pendingMessage);
        const message = ChannelMessage.decode(fullBytes);

        if (message.id) {
          if (this.#processedMessageIds.has(message.id)) {
            this.#logger.warn('Received duplicate message', {
              case: 'EventsQueue',
              lastReceivedMsgId,
              currentReceivedMsgId: message.id,
            });
          }

          if (message.id > lastReceivedMsgId + 1) {
            this.#logger.warn('Received out of order message', {
              case: 'EventsQueue',
              lastReceivedMsgId,
              currentReceivedMsgId: message.id,
            });
          }
        }

        this.#processedMessageIds.add(message.id);
        lastReceivedMsgId = message.id;
        this.push(message);
      } catch (e) {
        this.#logger.error('Failed to parse channel message', {
          error: serializeError(e),
          case: 'EventsQueue',
        });
      }
    };

    const onClose = (): void => {
      dataChannel.removeListener('message', onMessage);
      dataChannel.removeListener('close', onClose);
    };

    dataChannel.on('close', onClose);
    dataChannel.on('message', onMessage);

    this.#logger.info('Subscribed to data channel', {
      case: 'EventsQueue',
    });
  }

  private push(message: ChannelMessage): void {
    this.#queue.push(message);
    void this.process();
  }

  private async process(): Promise<void> {
    if (this.#paused || this.#processing) {
      return;
    }

    this.#processing = true;

    while (!this.#paused && this.#queue.length > 0) {
      const message = this.#queue.shift();

      if (!message) {
        continue;
      }

      try {
        if (message.channelEvent && this.#channelEventHandler) {
          this.#logger.debug('Handle channel event', {
            case: 'EventsQueue',
            id: message.id,
            type: channelEventTypeToJSON(message.channelEvent.eventType),
            event: message.channelEvent,
          });

          await this.#channelEventHandler(message.channelEvent);
          continue;
        }

        if (message.mediaEvent && this.#mediaEventHandler) {
          this.#logger.debug('Handle media event', {
            case: 'EventsQueue',
            id: message.id,
            type: mediaEventTypeToJSON(message.mediaEvent.eventType),
            event: message.mediaEvent,
          });

          await this.#mediaEventHandler(message.mediaEvent);
          continue;
        }

        this.#logger.warn('Received unknown message', { message, case: 'EventsQueue' });
      } catch (error) {
        this.#logger.error('Failed to handle event', {
          case: 'EventsQueue',
          id: message.id,
          error: serializeError(error),
        });
      }
    }

    this.#processing = false;

    // If new events were pushed during processing, process again
    if (!this.#paused && this.#queue.length > 0) {
      queueMicrotask(() => void this.process());
    }
  }

  private processMessageChunk(data: ArrayBuffer): PendingMessage | undefined {
    const bytes = new Uint8Array(data);

    if (bytes.length < CHUNK_HEADER_SIZE) {
      this.#logger.warn('Chunk too small', {
        case: 'EventsQueue',
        bytesLength: bytes.length,
      });

      return;
    }

    const { msgId, index, total } = this.parseMessageChunkHeader(bytes);
    const dataPart = bytes.slice(CHUNK_HEADER_SIZE);

    if (!this.#pendingMessagesBuffer.has(msgId)) {
      this.#pendingMessagesBuffer.set(msgId, { msgId, total, received: [] });
    }

    const entry: PendingMessage | undefined = this.#pendingMessagesBuffer.get(msgId);

    if (!entry) {
      this.#logger.warn('Chunk buffer entry not found for msgId', {
        msgId,
        case: 'EventsQueue',
      });

      return;
    }

    entry.received[index] = dataPart;
    entry.msgId = msgId;

    return entry;
  }

  private parseMessageChunkHeader(bytes: Uint8Array): { msgId: number; index: number; total: number } {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    return {
      msgId: view.getUint32(CHUNK_HEADER_MSG_ID_OFFSET),
      index: view.getUint32(CHUNK_HEADER_INDEX_OFFSET),
      total: view.getUint32(CHUNK_HEADER_TOTAL_OFFSET),
    };
  }

  private isPendingMessageFullyReceived(pendingMessage: PendingMessage): boolean {
    return pendingMessage.received.filter(Boolean).length === pendingMessage.total;
  }

  private finalizeMessageBytes(entry: PendingMessage): Uint8Array {
    const fullBytes = new Uint8Array(entry.received.reduce((sum, arr) => sum + arr.length, 0));
    let offset = 0;

    for (const chunk of entry.received) {
      fullBytes.set(chunk, offset);
      offset += chunk.length;
    }

    this.#pendingMessagesBuffer.delete(entry.msgId);

    return fullBytes;
  }

  pause(): void {
    this.#paused = true;
  }

  size(): number {
    return this.#queue.length;
  }

  resume(): void {
    this.#paused = false;
    void this.process();
  }

  isMessageReceived(messageId: number): boolean {
    return this.#processedMessageIds.has(messageId);
  }
}
